import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  HelpCircle,
  ShieldCheck,
  Star,
  WalletCards,
  XCircle,
} from "lucide-react";
import Footer from "./Footer";
import "./Pricing.css";

const fadeInUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07 } },
};

export default function Pricing() {
  const [billing, setBilling] = useState("monthly");
  const discountYearly = 0.2;

  const plans = useMemo(
    () => [
      {
        id: "free",
        name: "Free",
        tagline: "For testing KostPadi before you start properly.",
        priceMonthly: 0,
        priceQuarterly: 0,
        priceYearly: 0,
        allowedBilling: ["monthly", "quarterly", "yearly"],
        features: [
          "Create 1 business profile",
          "Add company details and contact information",
          "Basic dashboard summary",
          "Basic product/service list",
          "Basic customer records",
          "Community support",
        ],
        cta: { label: "Start Free", href: "/login" },
        highlight: false,
      },
      {
        id: "starter",
        name: "Starter",
        tagline:
          "For freelancers, solo founders, and small service businesses.",
        priceMonthly: 3000,
        priceQuarterly: 3000 * 3,
        priceYearly: Math.round(3000 * 12 * (1 - discountYearly)),
        allowedBilling: ["monthly", "quarterly", "yearly"],
        features: [
          "Everything in Free, plus:",
          "Create invoices, estimates, and receipts",
          "Download professional PDF documents",
          "Add your logo and business details",
          "Track paid and unpaid invoices",
          "Save customers and customer balances",
          "Basic sales and profit reports",
          "Standard support",
        ],
        cta: { label: "Choose Starter", href: "/login" },
        highlight: false,
      },
      {
        id: "sme",
        name: "SME",
        tagline: "For small businesses that sell products or manage suppliers.",
        priceMonthly: 12500,
        priceQuarterly: 12500 * 3,
        priceYearly: Math.round(12500 * 12 * (1 - discountYearly)),
        allowedBilling: ["quarterly", "yearly"],
        features: [
          "Everything in Starter, plus:",
          "Inventory and stock tracking",
          "Products, warehouses, and stock movement",
          "Purchases and supplier balances",
          "Part payment tracking",
          "Sales, purchases, profit, and inventory reports",
          "Automatic bookkeeping behind the scenes",
          "Customer and supplier mini statements",
        ],
        cta: { label: "Choose SME", href: "/login" },
        highlight: true,
        badgeNote: "SME is billed quarterly or yearly only.",
      },
      {
        id: "business",
        name: "Business",
        tagline:
          "For growing SMEs with staff, approvals, and advanced reports.",
        priceMonthly: 29999,
        priceQuarterly: 29999 * 3,
        priceYearly: Math.round(29999 * 12 * (1 - discountYearly)),
        allowedBilling: ["monthly", "quarterly", "yearly"],
        features: [
          "Everything in SME, plus:",
          "Up to 5 businesses under one account",
          "Up to 10 users included",
          "Dynamic user roles and permissions",
          "Super Admin, Accountant, HR, Marketing, and Sales/User access",
          "Payroll management",
          "Fixed asset register and depreciation",
          "Bank transactions and reconciliation",
          "Budgeting and FP&A",
          "CRM, booking, scheduling, tasks, and approvals",
          "Advanced reports: P&L, cash flow, balance sheet",
          "PDF, Excel, and CSV exports",
          "Priority support and onboarding guidance",
        ],
        cta: { label: "Talk to Sales", href: "/contact" },
        highlight: false,
      },
    ],
    []
  );

  const priceFor = (plan) => {
    if (plan.priceMonthly === 0) return 0;
    if (plan.id === "sme" && billing === "monthly") return null;
    if (billing === "monthly") return plan.priceMonthly;
    if (billing === "quarterly") return plan.priceQuarterly;
    return plan.priceYearly;
  };

  const periodLabel = () => {
    if (billing === "monthly") return "/mo";
    if (billing === "quarterly") return "/qtr";
    return "/yr";
  };

  return (
    <div className="pricing-root">
      <header className="kp-container pricing-hero" id="top">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          className="pricing-hero-inner"
        >
          <motion.span variants={fadeInUp} className="pricing-tag">
            Simple pricing for Nigerian SMEs
          </motion.span>

          <motion.h1 variants={fadeInUp}>
            Choose a plan that matches how your business works.
          </motion.h1>

          <motion.p variants={fadeInUp} className="pricing-lead">
            Start small, upgrade when you need inventory, payroll, reports, team
            access, CRM, budgeting, or advanced business controls.
          </motion.p>

          <motion.div
            variants={fadeInUp}
            className="billing-toggle"
            role="group"
            aria-label="Billing period"
          >
            <button
              type="button"
              className={`tgl ${billing === "monthly" ? "active" : ""}`}
              onClick={() => setBilling("monthly")}
            >
              Monthly
            </button>
            <button
              type="button"
              className={`tgl ${billing === "quarterly" ? "active" : ""}`}
              onClick={() => setBilling("quarterly")}
            >
              Quarterly
            </button>
            <button
              type="button"
              className={`tgl ${billing === "yearly" ? "active" : ""}`}
              onClick={() => setBilling("yearly")}
            >
              Yearly <span className="save">Save 20%</span>
            </button>
          </motion.div>

          <motion.p variants={fadeInUp} className="pricing-note">
            SME is billed quarterly or yearly only. Business is now{" "}
            <strong>₦29,999/month</strong>.
          </motion.p>
        </motion.div>
      </header>

      <section className="kp-container pricing-section">
        <div className="plans-grid">
          {plans.map((plan) => {
            const computedPrice = priceFor(plan);
            const isSMEMonthlyBlocked =
              plan.id === "sme" && billing === "monthly";

            return (
              <motion.div
                key={plan.id}
                variants={fadeInUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className={`plan-card ${plan.highlight ? "featured" : ""}`}
              >
                {plan.highlight && (
                  <div className="plan-ribbon">
                    <Star size={15} />
                    Most practical
                  </div>
                )}

                <div className="plan-head">
                  <h3>{plan.name}</h3>
                  <p className="plan-tagline">{plan.tagline}</p>

                  <div className="plan-price">
                    {plan.priceMonthly === 0 ? (
                      <>
                        <span className="naira">₦</span>
                        <span className="amount">0</span>
                        <span className="per">/mo</span>
                      </>
                    ) : isSMEMonthlyBlocked ? (
                      <span className="amount small">
                        Quarterly / Yearly only
                      </span>
                    ) : (
                      <>
                        <span className="naira">₦</span>
                        <span className="amount">
                          {Number(computedPrice).toLocaleString("en-NG")}
                        </span>
                        <span className="per">{periodLabel()}</span>
                      </>
                    )}
                  </div>

                  {billing === "yearly" && plan.priceMonthly > 0 && (
                    <div className="strike">
                      ₦{(plan.priceMonthly * 12).toLocaleString("en-NG")} before
                      discount
                    </div>
                  )}

                  {plan.badgeNote && (
                    <div className="plan-note">{plan.badgeNote}</div>
                  )}
                </div>

                <ul className="plan-features">
                  {plan.features.map((feature) => (
                    <li key={feature}>
                      <CheckCircle2 size={16} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <a
                  className={`pricing-btn ${
                    plan.highlight ? "pricing-btn-primary" : "pricing-btn-ghost"
                  }`}
                  href={plan.cta.href}
                >
                  {plan.cta.label} <ArrowRight size={17} />
                </a>
              </motion.div>
            );
          })}
        </div>

        <div className="addons">
          <div className="pricing-section-head">
            <span className="pricing-mini-tag">Add-ons</span>
            <h2>Add extra support as your business grows.</h2>
          </div>

          <div className="addons-grid">
            {addons.map((addon) => (
              <div className="addon" key={addon.name}>
                <div className="addon-head">
                  <strong>{addon.name}</strong>
                  <span>{addon.price}</span>
                </div>
                <p>{addon.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pricing-section pricing-section-alt">
        <div className="kp-container">
          <div className="pricing-section-head">
            <span className="pricing-mini-tag">Plan comparison</span>
            <h2>Compare KostPadi plans in plain English.</h2>
          </div>

          <div className="compare-wrap">
            <table className="compare">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Free</th>
                  <th>Starter</th>
                  <th>SME</th>
                  <th>Business</th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((row) => (
                  <tr key={row.feature}>
                    <td className="feat">{row.feature}</td>
                    <td>{cell(row.free)}</td>
                    <td>{cell(row.starter)}</td>
                    <td>{cell(row.sme)}</td>
                    <td>{cell(row.business)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="kp-container pricing-section">
        <div className="pricing-section-head">
          <span className="pricing-mini-tag">KostPadi vs alternatives</span>
          <h2>Why many Nigerian SMEs may prefer KostPadi.</h2>
          <p>
            Other ERP solutions such as QuickBooks and Zoho are strong global
            tools, but KostPadi is built around local SME realities: naira
            pricing, Nigerian VAT/WHT context, simple wording, payroll,
            inventory, CRM, booking, budgeting, and role-based access in one
            practical workspace.
          </p>
        </div>

        <div className="competitor-grid">
          {competitorRows.map((row) => (
            <div className="competitor-row" key={row.point}>
              <div className="competitor-point">{row.point}</div>
              <div className="competitor-cell kostpadi">
                <BadgeCheck size={18} />
                <span>{row.kostpadi}</span>
              </div>
              <div className="competitor-cell">
                {row.quickbooksGood ? (
                  <CheckCircle2 size={18} />
                ) : (
                  <HelpCircle size={18} />
                )}
                <span>{row.quickbooks}</span>
              </div>
              <div className="competitor-cell">
                {row.zohoGood ? (
                  <CheckCircle2 size={18} />
                ) : (
                  <HelpCircle size={18} />
                )}
                <span>{row.zoho}</span>
              </div>
            </div>
          ))}
        </div>

        <p className="competitor-note">
          This comparison is written for easy decision-making and may vary by
          country, plan, add-ons, and exchange rates.
        </p>
      </section>

      <section className="pricing-section pricing-section-alt">
        <div className="kp-container assurance">
          <AssuranceCard
            icon={WalletCards}
            title="Pay in naira"
            text="No exchange-rate surprise for your core subscription."
          />
          <AssuranceCard
            icon={ShieldCheck}
            title="Secure access"
            text="Role-based access helps control who can see and do what."
          />
          <AssuranceCard
            icon={BadgeCheck}
            title="Built for SMEs"
            text="Simple wording, practical workflows, and Nigerian business context."
          />
        </div>
      </section>

      <section className="kp-container pricing-section">
        <div className="pricing-cta-card">
          <div>
            <h2>Start free. Upgrade when your business needs more.</h2>
            <p>
              Try KostPadi today and move from scattered records to clearer
              business control.
            </p>
          </div>

          <div className="pricing-cta-actions">
            <a className="pricing-btn pricing-btn-primary" href="/login">
              Create account <ArrowRight size={17} />
            </a>
            <a className="pricing-btn pricing-btn-ghost light" href="/contact">
              Talk to Sales
            </a>
          </div>
        </div>
      </section>

      <section className="pricing-section pricing-section-alt">
        <div className="kp-container">
          <div className="pricing-section-head">
            <span className="pricing-mini-tag">Questions</span>
            <h2>Pricing FAQs</h2>
          </div>

          <div className="pricing-faq-grid">
            {faqs.map((faq) => (
              <div className="pricing-faq-item" key={faq.q}>
                <h3>{faq.q}</h3>
                <p>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function cell(val) {
  if (val === "✓") return <CheckCircle2 className="yes" size={18} />;
  if (val === "—") return <XCircle className="no" size={18} />;
  return val;
}

function AssuranceCard({ icon: Icon, title, text }) {
  return (
    <div className="assure-card">
      <div className="pricing-icon-wrap">
        <Icon size={22} />
      </div>
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
}

const addons = [
  {
    name: "Extra Users",
    desc: "Add more seats as your team grows.",
    price: "₦1,500 / user / mo",
  },
  {
    name: "Implementation Pack",
    desc: "Onboarding, data import, setup, and training.",
    price: "From ₦150,000",
  },
  {
    name: "Dedicated Success Manager",
    desc: "Quarterly reviews, reporting support, and roadmap alignment.",
    price: "Custom",
  },
];

const comparison = [
  {
    feature: "Invoices, estimates, and receipts",
    free: "—",
    starter: "✓",
    sme: "✓",
    business: "✓",
  },
  {
    feature: "Branded PDF documents",
    free: "—",
    starter: "✓",
    sme: "✓",
    business: "✓",
  },
  {
    feature: "Customer records and balances",
    free: "Basic",
    starter: "✓",
    sme: "✓",
    business: "✓",
  },
  {
    feature: "Invoices per month",
    free: "0",
    starter: "20",
    sme: "Unlimited",
    business: "Unlimited",
  },
  {
    feature: "Inventory and stock tracking",
    free: "—",
    starter: "—",
    sme: "✓",
    business: "✓",
  },
  {
    feature: "Purchases and supplier balances",
    free: "—",
    starter: "—",
    sme: "✓",
    business: "✓",
  },
  {
    feature: "Part payments",
    free: "—",
    starter: "—",
    sme: "✓",
    business: "✓",
  },
  {
    feature: "Payroll management",
    free: "—",
    starter: "—",
    sme: "—",
    business: "✓",
  },
  { feature: "CRM tool", free: "—", starter: "—", sme: "—", business: "✓" },
  {
    feature: "Booking and scheduling",
    free: "—",
    starter: "—",
    sme: "—",
    business: "✓",
  },
  {
    feature: "Budgeting and FP&A",
    free: "—",
    starter: "—",
    sme: "—",
    business: "✓",
  },
  {
    feature: "Fixed asset register",
    free: "—",
    starter: "—",
    sme: "—",
    business: "✓",
  },
  {
    feature: "Bank transactions and reconciliation",
    free: "—",
    starter: "—",
    sme: "—",
    business: "✓",
  },
  {
    feature: "Advanced financial reports",
    free: "—",
    starter: "Basic",
    sme: "Standard",
    business: "✓",
  },
  {
    feature: "Multi-user roles",
    free: "—",
    starter: "—",
    sme: "—",
    business: "✓",
  },
  {
    feature: "Users included",
    free: "1",
    starter: "1",
    sme: "1",
    business: "Up to 10",
  },
  {
    feature: "Support",
    free: "Community",
    starter: "Standard",
    sme: "Standard+",
    business: "Priority",
  },
];

const competitorRows = [
  {
    point: "Pricing",
    kostpadi: "Naira pricing from ₦3,000/month",
    quickbooks: "Usually USD-based depending on country/plan",
    zoho: "Often USD/local-market pricing depending on product",
    quickbooksGood: false,
    zohoGood: false,
  },
  {
    point: "Ease for Nigerian SMEs",
    kostpadi: "Uses simple, local business language",
    quickbooks: "Powerful, but can feel accounting-heavy",
    zoho: "Strong, but may require combining Zoho apps",
    quickbooksGood: false,
    zohoGood: false,
  },
  {
    point: "VAT/WHT context",
    kostpadi: "Built around Nigerian VAT/WHT workflows",
    quickbooks: "Tax setup depends on country configuration",
    zoho: "Tax setup depends on region and plan",
    quickbooksGood: false,
    zohoGood: false,
  },
  {
    point: "Inventory",
    kostpadi: "Included from SME plan",
    quickbooks: "Available on higher plans",
    zoho: "May require Zoho Inventory for deeper inventory",
    quickbooksGood: false,
    zohoGood: false,
  },
  {
    point: "Payroll + accounting",
    kostpadi: "Payroll connects with bookkeeping flow",
    quickbooks: "Payroll may depend on country/add-on",
    zoho: "Payroll availability depends on region/product",
    quickbooksGood: false,
    zohoGood: false,
  },
  {
    point: "All-in-one SME operations",
    kostpadi: "Finance, payroll, CRM, booking, tasks, budgeting, inventory",
    quickbooks: "Strong accounting focus with integrations",
    zoho: "Very strong ecosystem, but often across separate apps",
    quickbooksGood: false,
    zohoGood: false,
  },
];

const faqs = [
  {
    q: "Can I stay on Free forever?",
    a: "Yes. Free is good for testing KostPadi. Upgrade when you need invoices, inventory, payroll, reports, or team access.",
  },
  {
    q: "Is SME available monthly?",
    a: "No. SME is billed quarterly or yearly only.",
  },
  {
    q: "Is VAT included?",
    a: "No. Prices shown exclude VAT. Taxes may apply at checkout.",
  },
  {
    q: "Can I cancel or switch plans?",
    a: "Yes. You can switch plans as your business needs change.",
  },
  {
    q: "Why choose KostPadi over QuickBooks or Zoho?",
    a: "KostPadi is built with Nigerian SMEs in mind: naira pricing, simple wording, VAT/WHT context, and practical tools beyond accounting.",
  },
  {
    q: "Does Business include payroll and multi-user access?",
    a: "Yes. Business includes payroll, advanced reports, fixed assets, bank reconciliation, CRM, budgeting, and team roles.",
  },
];
